/* eslint-disable */
// @ts-nocheck
import axios, { AxiosError, AxiosHeaders, AxiosRequestConfig } from 'axios'
import base64Url from 'base64url'
import debounce from 'lodash/fp/debounce'
import { window } from '@mtsbank/ui-kit'
import { AuthParams, SsoParams } from '@entities/user/model/auth.types'
import { formatPhoneNumber, FormatAliasPhone } from '@shared/lib/phone'
import { removeTidCookie, getTid } from '@shared/lib/gib'
import { generateUuid } from '@shared/lib'
import { getMtsIDTokenApi } from '@root/api/sso/api'
import { AuthData, AuthType, LocalStorageKey, UUID, MtsData, SetMtsDataStore } from './types'
import { TypedStorage } from './typedStorage'
import { parseJwt } from './parseJwt'

const LOGOUT_TIME = 900000 // 15 minutes in ms
const DELAY_DEBOUNCE = 3000 // 3 seconds in ms
const MAX_32_BIT = 0x7fffffff // Максимально допустимое значение в setTimeOut

class AuthHelper {
  private activityTimerId: number

  private logoutTimerId: number

  private authData: AuthData
  private mtsData: MtsData

  // TODO реализовать профиль пользователя как будет готова аналитика

  public tid: string
  public state: string
  public count: number = 0

  private _lastActivityTime: number | null = null
  private _timerInfoInterval: number | null = null
  private _isLoggingOut = false

  private handleAuthorizationError = (error: Error): void => {
    console.log(`Ошибка: ${error.message}`)
  }

  private validateParams(params: (string | undefined)[]): void {
    if (params.some((param) => !param)) {
      throw new Error('Не все обязательные параметры были предоставлены.')
    }
  }

  constructor() {
    this.authData = this.getAuthData()
    this.mtsData = this.getMtsData()
    this.state = this.getSavedUUID(UUID.STATE)
    this.tid = getTid()
    this.count = 0
  }

  public getSavedUUID = (uuidName: UUID) => {
    let currentUUID = window?.localStorage.getItem(uuidName)
    if (!currentUUID) {
      currentUUID = generateUuid()
      window.localStorage.setItem(uuidName, currentUUID)
    }

    return currentUUID
  }

  public doDeleteSavedUUIDS = () => {
    window?.localStorage.setItem(UUID.STATE, '')
    window?.localStorage.setItem(UUID.TID, '')
  }

  /**
   * Инициализация параметров перед редиректом на страницу авторизации MTS
   */
  private mtsIdInit = (
    authUrl: string,
    clientId: string,
    redirectUri: string,
    scope: string,
    service?: string,
    responseType: string,
    theme: string,
    prompt: string,
    realm: string
  ): string => {
    const url: string = authUrl

    // Подготовка параметров запроса
    const queryParams = new URLSearchParams({
      client_id: clientId,
      tid: this.tid,
      redirect_uri: redirectUri,
      scope,
      state: this.state,
      response_type: responseType,
      theme,
      nonce: generateUuid(),
      prompt, // Возможные значения: login, consent, none
      realm, // Область аутентификации пользователя
    })

    return `${url}?${queryParams.toString()}`
  }

  /**
   * Метод редиректа
   */
  public redirectTo = (pageUrl: string): void => {
    try {
      window.location.assign(pageUrl)
    } catch (error) {
      // Fallback вариант, если assign не сработает
      window.location.replace(pageUrl)
    }
  }

  /**
   * Авторизация по МТС ID
   */
  public mtsIdAuthorize = (authData: AuthParams) => {
    try {
      // logMtsIdAuthorizeInit(authData)
      const {
        auth: { authUrl, clientId, redirectUri, scope, service, responseType, theme, prompt, realm },
      } = authData

      // Проверка наличия всех обязательных параметров
      this.validateParams([authUrl, clientId, redirectUri, scope, service, responseType, theme, prompt, realm])

      const authorizeUrl = this.mtsIdInit(
        authUrl,
        clientId,
        redirectUri,
        scope,
        service,
        responseType,
        theme,
        prompt,
        realm
      )
      // logMtsIdAuthorizeSuccess(authorizeUrl)
      return Promise.resolve(authorizeUrl)
    } catch (error) {
      // logMtsIdAuthorizeError(error?.message, authData)
      this.handleAuthorizationError(error)

      return Promise.reject(error)
    }
  }

  /**
   * Обмен кода авторизации на токен
   * (Вызывается после получения кода от MTS)
   */
  // TODO переименовать метод | отрефачить
  public getAuthToken(code: string, authData: SsoParams) {
    const requestId = generateUuid() as string

    try {
      // logInitValidateParams(Boolean(code), authData)
      const {
        sso: { ssoUrl, redirectSsoUri, grantType },
        auth: { clientId },
      } = authData

      // Проверяем наличие всех обязательных параметров
      this.validateParams([code, ssoUrl, clientId, redirectSsoUri, grantType])

      const baseUrl: string = ssoUrl

      // Подготовка параметров запроса
      const queryParams = {
        code,
        grant_type: grantType,
        mts_redirect_uri: redirectSsoUri,
        redirect_uri: redirectSsoUri,
        tid: this.tid,
        state: this.state,
      }

      // Формируем URL для GET-запроса на proxySSO
      const url = `${baseUrl}`
      // logStartGetAuthToken(requestId)

      // Возвращаем результат вызова axios.post(), чтобы вернуть промис резолв
      return axios
        .post(
          url,
          { ...queryParams },
          {
            headers: {
              'Client-Id': clientId,
              'X-Request-Id': requestId,
              'X-MTS-ID': false,
            },
          }
        )
        .then((response) => {
          const token: string = response.data.access_token
          this.setToken(token)

          // Принудительно обновляем authData
          this.authData = this.getAuthData()

          // Проверяем, что authData действительно обновился
          if (this.authData) {
            console.log('Token set successfully, expiry:', this.authData.expiry)
            this.initLogout()
            this.initActivity()
          } else {
            console.error('Failed to update authData after setting token')
          }
        })
        .catch((error: AxiosError) => {
          // console.logGetAuthToken(error, authData, Boolean(code))

          throw error
        })
    } catch (error) {
      console.log(`Ошибка валидации параметров: ${error.message}`)
      // console.logValidateParams(error.message, authData, Boolean(code))
      return Promise.reject(error)
    }
  }

  private setBackURL = () => {
    TypedStorage.setItem(LocalStorageKey.BACK_TO_URL, window.location.href.replace('/cyber', ''))
  }

  /**
   * При авторизации устанавливается sso_returnUrl, после авторизации происходит редирект на указанный uri
   */
  public onSignIn = () => {
    this.setBackURL()
  }

  public getBackURL = () => {
    try {
      const requestUrl = new URL(TypedStorage.getString(LocalStorageKey.BACK_TO_URL))

      return requestUrl.href.replace(`${requestUrl.protocol}//${requestUrl.host}`, '')
    } catch (error) {
      console.log(error)

      return ''
    }
  }

  public resetBackUrl = () => {
    TypedStorage.removeItem(LocalStorageKey.BACK_TO_URL)
  }

  /**
   * Время истечения токена
   * @private
   */
  private get expiry() {
    return new Date(this?.authData?.expiry).getTime()
  }

  /**
   * clientId для web dbo
   * @public
   */
  public get clientId() {
    return this.authData?.client_id
  }

  /**
   * Данные авторизованного пользователя
   * @private
   */
  private get user() {
    return this.authData?.user_data
  }

  /**
   * Конфигурация axios, добавляет cookie и токен в заголовки запроса
   */
  public configureAxios = () => {
    axios.defaults.withCredentials = true
    axios.defaults.headers.common['Content-Type'] = 'application/json;charset=UTF-8'

    axios.interceptors.request.use((config: AxiosRequestConfig) => {
      if (!this.isAuthenticated()) {
        return config
      }

      const headers = {
        Authorization: this.getAuthHeader(),
        // 'Client-Id': this.clientId,
        // 'X-Request-Id': uuid(),
        platform: 'web',
        ...config.headers,
      }

      return Object.assign(config, {
        headers,
      })
    })

    axios.interceptors.response.use(
      (response) => {
        const {
          headers: { authorization },
        } = response

        if (response.data && response.data.redirect_url === '/authorize') {
          this.logOut()
          return response
        }

        if (authorization) {
          const authToken: string = authorization.split(' ').pop()

          this.setToken(authToken)
          this.authData = this.getAuthData()

          this.initLogout()
          this.initActivity()
        }

        return response
      },
      (error: AxiosError) => {
        if (error?.response) {
          if (error.response.status === 401) {
            this.logOut()
          }
        }

        return Promise.reject(error)
      }
    )
  }

  /**
   * Возвращает заголовок вида 'Bearer {token}'
   */
  public getAuthHeader = (): string => `Bearer ${this.getToken()}`

  /**
   * Возвращает токен в случае его наличия в localStorage
   */
  private getToken = (): string | null => TypedStorage.getString(LocalStorageKey.ACCESS_TOKEN)

  /**
   * Сохраняет токен в localStorage
   * @param token
   */
  setToken = (token: string) => TypedStorage.setItem(LocalStorageKey.ACCESS_TOKEN, token)

  /**
   * Удаление токена авторизации из localStorage
   */
  private removeToken = () => TypedStorage.removeItem(LocalStorageKey.ACCESS_TOKEN)
  private removeMtsIdToken = () => TypedStorage.removeItem('mts_id_token')
  private removeTid = () => TypedStorage.removeItem('tid')

  private deleteAllClientData = () => {
    this.removeToken()
    this.removeMtsIdToken()
    this.doDeleteSavedUUIDS()
    removeTidCookie()
    this.count = 0 // Обнуляем счетчик при удалении данных
  }

  /**
   * Получение данных из токена авторизации в виде объекта
   */
  public getAuthData = (): AuthData | null => {
    let authData: AuthData

    try {
      const authDataEncode = this.getToken().split('.').pop()

      authData = JSON.parse(base64Url.decode(authDataEncode))
    } catch (error) {
      authData = null
    }

    return authData
  }

  /**
   * Получение профиля пользователя
   */
  // public loadProfile = () => {
  //   if (!this.isAuthenticated()) {
  //     return Promise.resolve(false)
  //   }

  //   try {
  //     const { profile } = useAuthDataStore.getState()

  //     if (!isEmpty(profile)) {
  //       this.profile = profile
  //     } else {
  //       return this.fetchProfile()
  //     }
  //   } catch {
  //     return this.fetchProfile()
  //   }
  // }

  /**
   * Загрузка профиля пользователя
   */
  // fetchProfile = async () => {
  //   const headers: Headers = {}
  //   const token = localStorage.getItem('access_token')

  //   const { setProfile } = useAuthDataStore.setState()

  //   if (token) {
  //     headers.Authorization = `Bearer ${token}`

  //     try {
  //       const response = await ewalletProfileApi.getWallet()

  //       const mobilePhone = formatPhoneNumber(response.data.phone, FormatAliasPhone.SYMBOLS12)

  //       // window.gib.setIdentityAndEncrypt(formatPhoneNumber(response.data.phone, FormatAliasPhone.DIGIT11))

  //       this.profile = {
  //         mobilePhone,
  //         authType: AuthType[response.data?.walletIdentificationLevel],
  //         userId: response.data?.userId,
  //       }

  //       if (mobilePhone && window.gib && typeof window.gib.setIdentityAndEncrypt === 'function') {
  //         window.gib.setIdentityAndEncrypt(formatPhoneNumber(response.data.phone, FormatAliasPhone.DIGIT11))
  //         window.gib.setAuthStatus(window.gib.IS_AUTHORIZED)
  //       }

  //       setProfile(this.profile)
  //     } catch (error) {}
  //   }
  // }

  /**
   * Поолучения данных пользователя из токена авторизации
   */
  public get userData() {
    return this.user
  }

  public get fullUserInfo() {
    return this.authData
  }

  public initMtsToken = (setMtsDataStore: SetMtsDataStore) => {
    const headers = {} as AxiosHeaders
    const token = localStorage.getItem('access_token')
    headers.Authorization = `Bearer ${token}`
    const currentMtsIdToken = localStorage.getItem('mts_id_token')

    if (!currentMtsIdToken) {
      getMtsIDTokenApi({ headers }).then(({ data: { mts_id_token } }) => {
        window.localStorage.setItem('mts_id_token', mts_id_token)

        const parsedMtsData = parseJwt(mts_id_token) as MtsData
        setMtsDataStore(parsedMtsData)
        window.gib.setIdentityAndEncrypt(formatPhoneNumber(parsedMtsData?.phone, FormatAliasPhone.DIGIT11))
      })
    } else {
      setMtsDataStore(parseJwt(currentMtsIdToken) as MtsData)
    }
  }

  public getMtsData = () => {
    const mtsIdToken = window.localStorage.getItem('mts_id_token')
    return parseJwt(mtsIdToken)
  }

  public getPhone = () => {
    return this.getMtsData()?.phone
  }

  /**
   * Функция для форматирования времени в мм:сс
   */
  private formatTimeRemaining(milliseconds: number): string {
    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`
  }

  // Логирование информации о таймерах
  private logTimersInfo(): void {
    if (this.activityTimerId) {
      // Получаем оставшееся время для таймера активности
      const activityTimerInfo = window.performance.now() - (this._lastActivityTime || 0)
      const activityTimeRemaining = LOGOUT_TIME - activityTimerInfo

      console.log(
        `Таймер активности: ID=${this.activityTimerId}, осталось ${this.formatTimeRemaining(activityTimeRemaining)}`
      )
    }

    if (this.logoutTimerId && this.authData?.expiry) {
      // Получаем оставшееся время для таймера истечения токена
      const tokenExpiryTime = new Date(this.authData.expiry).getTime()
      const tokenTimeRemaining = tokenExpiryTime - Date.now()

      console.log(`Таймер токена: ID=${this.logoutTimerId}, осталось ${this.formatTimeRemaining(tokenTimeRemaining)}`)
    }
  }

  /**
   * Осуществляется проверка активности пользователя
   * если пользователь проявил активность в установленное время, то время до разлогинивания продлевается
   *
   * @private
   */
  private checkActivity = debounce(DELAY_DEBOUNCE, () => {
    if (this.activityTimerId) {
      clearTimeout(this.activityTimerId)
    }

    // Сохраняем время последней активности
    this._lastActivityTime = window.performance.now()

    this.activityTimerId = window.setTimeout(() => this.logOut(), LOGOUT_TIME)
    console.log(
      `Таймер активности обновлен: ID=${this.activityTimerId}, время до разлогина: ${this.formatTimeRemaining(
        LOGOUT_TIME
      )}`
    )

    // Логируем информацию о всех таймерах
    this.logTimersInfo()
  })

  /**
   * Инициализация отслеживания активности пользователя
   * в случае установленного времени бездействия  происходит разлогинивание
   */
  private initActivity = () => {
    if (this.isAuthenticated()) {
      this.checkActivity()

      document.removeEventListener('keypress', this.checkActivity)
      document.removeEventListener('mousedown', this.checkActivity)
      document.removeEventListener('mousemove', this.checkActivity)
      document.removeEventListener('touchstart', this.checkActivity)

      document.addEventListener('keypress', this.checkActivity)
      document.addEventListener('mousedown', this.checkActivity)
      document.addEventListener('mousemove', this.checkActivity)
      document.addEventListener('touchstart', this.checkActivity)
    }
  }

  /**
   * Инициализация таймера для разлогинивания пользователя в случае, если истечёт время токена авторизации
   */
  private initLogout = () => {
    if (this.logoutTimerId) {
      clearTimeout(this.logoutTimerId)
    }

    const expiryTime = this?.expiry ?? 0

    if (this.isAuthenticated() && expiryTime > 0) {
      const logoutTime = Math.min(expiryTime - Date.now(), MAX_32_BIT)

      this.logoutTimerId = window.setTimeout(() => this.logOut(), logoutTime)
      console.log(
        `Таймер токена установлен: ID=${this.logoutTimerId}, время до разлогина: ${this.formatTimeRemaining(
          logoutTime
        )}`
      )

      // Устанавливаем интервал для периодического логирования
      if (this._timerInfoInterval) {
        clearInterval(this._timerInfoInterval)
      }

      this._timerInfoInterval = window.setInterval(() => {
        this.logTimersInfo()
      }, 60000) // Логируем каждую минуту
    }
  }

  /**
   * Возвращает признак состояния аутентификации
   */
  public isAuthenticated = (): boolean => {
    if (!this.authData) {
      return false
    }

    return this?.expiry > Date.now()
  }

  /**
   * Проверка и удаление просроченного токена
   */
  private checkAndCleanExpiredToken = (): void => {
    try {
      const token = this.getToken()
      if (!token) return

      const authData = this.getAuthData()
      if (!authData) {
        this.deleteAllClientData()
        return
      }

      const expiryTime = new Date(authData.expiry).getTime()
      const now = Date.now()

      // Если токен просрочен
      if (expiryTime <= now) {
        this.deleteAllClientData()
      }
    } catch (error) {
      // При любой ошибке лучше удалить токен
      this.deleteAllClientData()
    }
  }

  /**
   * Инициализация хелпера, желательно выполнить один раз при запуске приложения
   */
  public initialize = () => {
    try {
      // Сначала проверяем и очищаем просроченный токен
      this.checkAndCleanExpiredToken()

      // Затем инициализируем остальные компоненты
      if (this.isAuthenticated()) {
        this.initActivity()
        this.initLogout()
      }

      this.configureAxios()
    } catch (error) {
      console.log(`Ошибка в процессе инициализации: ${error.message}`)
    }
  }

  /**
   * Проверка на наличие доступа в зависимости от типа идентификации
   *
   * @param authType
   * @param profileType
   */
  public hasAccess = (authType: AuthType, profileType: AuthType) => (profileType ? authType <= profileType : false)

  /**
   * Поолучения данных пользователя из токена авторизации
   */
  public get userData() {
    return this.user
  }

  // TODO очистить таймер и вызвать метод по ссо лог аут

  public logOut = async () => {
    if (this._isLoggingOut) return
    this._isLoggingOut = true

    if (this.activityTimerId) {
      clearTimeout(this.activityTimerId)
      this.activityTimerId = null
    }

    if (this.logoutTimerId) {
      clearTimeout(this.logoutTimerId)
      this.logoutTimerId = null
    }

    // Очищаем интервал логирования при выходе
    if (this._timerInfoInterval) {
      clearInterval(this._timerInfoInterval)
      this._timerInfoInterval = null
    }

    try {
      await axios
        .get(`${process.env.NEXT_PUBLIC_SSO_URL}/logout`, {
          headers: {
            'Client-Id': this.clientId,
            Authorization: this.getAuthHeader(),
          },
        })
        .then(() => {})
        .catch((error) => {
          // console.log(error)
        })
    } catch (error) {
      this.handleAuthorizationError(error)
    } finally {
      this.deleteAllClientData()
      if (window.gib) {
        window.gib.setAuthStatus(window.gib.IS_GUEST)
      }
      this.redirectTo(`${window.location.origin}/cyber`)
    }
  }

  /**
   * walletId - идентификатор клиента lewis
   * если есть walletId, то клиент является клиентом lewis
   * не гарантирует наличие пр
   */
  public get walletId() {
    return this.user?.wallet_id
  }
}

export const authHelper = new AuthHelper()
